import { useNavigate } from 'react-router';
import type { GraphId, NodeId } from '../types/graph';
import { match, P } from 'ts-pattern';
import { useStore } from '@/store';

/**
 * A graph can only be part of another graph as a definition of a property of the parent graph.
 * These properties define the information required to establish a link.
 */
export type GraphNavigationState = {
  parentGraphId: GraphId;
  ownerNodeId: NodeId;
  paramKey: string;
};

/**
 * A graph can either link to an entry directly, or the graph defines the value for a property inside another graph.
 * In the former case, entryId and graphId (if it exists), have a direct correlation and state is undefined.
 * In the latter case, the graphId (if it exists) indirectly links to entryId through one or more other graphs, state is defined.
 *
 * When either an entryId and/or a state is supplied, but not a graphId, navigation will create a new graph.
 */
export type GraphNavigationTarget = {
  entryId: string;
  graphId?: GraphId;
  state?: GraphNavigationState;
};

export const useGraphNavigation = () => {
  const navigate = useNavigate();
  const templateId = useStore(({ config: c }) => c.getId());

  const openGraph = ({ entryId, graphId, state }: GraphNavigationTarget) => {
    const path = match(graphId)
      .with(P.string, (id) => `/${templateId}/entries/${entryId}/graph/${id}`)
      .with(P.nullish, () => `/${templateId}/entries/${entryId}/graph`)
      .exhaustive();

    navigate(path, { state });
  };

  return {
    openGraph,
  };
};
